import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmt } from "@/lib/api";
import KpiTile from "@/components/KpiTile";
import StatusBadge from "@/components/StatusBadge";
import AutopilotTile from "@/components/AutopilotTile";
import FleetHeatmap from "@/components/FleetHeatmap";
import FamilyProgressionCard from "@/components/FamilyProgressionCard";
import StoreAnalyticsTile from "@/components/StoreAnalyticsTile";
import CycleProgressTile from "@/components/CycleProgressTile";
import BridgeHealthTile from "@/components/BridgeHealthTile";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, BarChart, Bar,
} from "recharts";
import {
  RobotIcon, TrophyIcon, SkullIcon, FlaskIcon, ChartLineUpIcon, ArrowRightIcon, BrainIcon, ClockClockwiseIcon, TargetIcon,
} from "@phosphor-icons/react";

const STATUS_COLORS = {
  champion: "#D6FF38",
  elite: "#60A5FA",
  basic: "#22C55E",
  testing: "#F97316",
  graveyard: "#FF4D4D",
};

const CHART_MARGIN = { top: 8, right: 8, left: 0, bottom: 0 };
const TOOLTIP_STYLE = { background: "#15171E", border: "1px solid #2A2D3A", borderRadius: 6, fontSize: 12 };
const TOOLTIP_LABEL_STYLE = { color: "#94A3B8" };
const AXIS_LINE_STROKE = { stroke: "#2A2D3A" };

export default function Overview() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/overview").then((r) => setData(r.data)).catch(console.error);
  }, []);

  if (!data) return <div className="p-6 text-[#64748B] mono text-sm">Loading control room…</div>;

  const { kpis, leader, top10, mutation_targets, recent_decisions, by_status, symbols } = data;
  const equityData = (leader?.monthly_results || []).map((m) => ({
    month: m.month, balance: m.final_balance, profit: m.profit,
  }));
  const statusData = Object.entries(by_status).map(([k, v]) => ({ name: k, value: v }));

  return (
    <div data-testid="overview-page" className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] mono uppercase tracking-widest text-[#D6FF38]/70">Control Room</div>
          <h1 className="text-xl md:text-2xl tracking-tight font-medium mt-1 text-[#F1F5F9]">Overview</h1>
        </div>
        <div className="text-[11px] mono text-[#64748B]">
          fleet · {kpis.total_bots} bots · WR {kpis.fleet_win_rate}%
        </div>
      </div>

      {/* KPI tiles - responsive grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
        <KpiTile testid="kpi-total" label="Bots Tested" value={fmt.int(kpis.total_bots)} icon={RobotIcon} sub="across all generations" />
        <KpiTile testid="kpi-champions" label="Champions" value={kpis.champions} accent icon={TrophyIcon} sub="promoted tier" />
        <KpiTile testid="kpi-elite" label="Elite" value={kpis.elite} tone="info" icon={ChartLineUpIcon} sub="promising" />
        <KpiTile testid="kpi-basic" label="Basic" value={kpis.basic ?? 0} tone="basic" icon={ChartLineUpIcon} sub="steady, keep testing" />
        <KpiTile testid="kpi-testing" label="Testing" value={kpis.testing ?? 0} tone="warn" icon={FlaskIcon} sub="under evaluation" />
        <Link to="/hall-of-fame" data-testid="kpi-hall-of-fame-link" className="block">
          <KpiTile testid="kpi-hall-of-fame" label="Hall of Fame" value={kpis.hall_of_fame ?? 0} tone="hof" icon={TrophyIcon} sub="top 10 leaderboard →" />
        </Link>
        <KpiTile testid="kpi-graveyard" label="Graveyard" value={kpis.graveyard} tone="loss" icon={SkullIcon} sub="killed" />
        <KpiTile testid="kpi-winrate" label="Fleet Win-Months" value={`${kpis.fleet_win_rate}%`} tone="profit" icon={TargetIcon} sub={`${kpis.profitable_months} / ${kpis.profitable_months + kpis.losing_months}`} />
      </div>

      {/* Autopilot live tile */}
      <AutopilotTile />

      {/* v6.5.30 Live cycle progress — from progress_reporter.ps1 on VPS */}
      <CycleProgressTile />

      {/* v6.5.44 Autonomous bridge self-heal status */}
      <BridgeHealthTile />

      {/* v6.5.22 Storefront analytics — traffic, waitlist and checkout stats */}
      <StoreAnalyticsTile />

      {/* Family Progression widget - diversification pivot tracker */}
      <FamilyProgressionCard />

      {/* Leader equity + status donut */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
        <div className="lg:col-span-3 border border-[#2A2D3A] bg-[#15171E] rounded-md p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] mono uppercase tracking-widest text-[#D6FF38]/70">Current Leader · Compound Equity</div>
              {leader && (
                <div className="mt-1 flex items-center gap-3">
                  <Link to={`/bot/${leader.id}`} data-testid="leader-link" className="mono text-base hover:text-[#D6FF38]">
                    {leader.name}
                  </Link>
                  <span className="text-[#64748B] text-sm">·</span>
                  <span className="mono text-sm text-[#94A3B8]">{leader.symbol}</span>
                  <StatusBadge status={leader.status} />
                  <span className="mono text-sm" style={{ color: leader.growth_pct >= 0 ? "#2ECA7F" : "#FF4D4D" }}>
                    {fmt.signed(leader.growth_pct)}%
                  </span>
                </div>
              )}
            </div>
            {leader && (
              <Link to={`/bot/${leader.id}`} className="text-xs mono uppercase tracking-widest text-[#D6FF38] flex items-center gap-1 hover:gap-2 transition-all">
                inspect <ArrowRightIcon size={12} />
              </Link>
            )}
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={equityData} margin={CHART_MARGIN}>
                <defs>
                  <linearGradient id="gLeader" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#D6FF38" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#D6FF38" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="2 4" stroke="#2A2D3A" opacity={0.4} />
                <XAxis dataKey="month" tickLine={false} axisLine={AXIS_LINE_STROKE} />
                <YAxis tickLine={false} axisLine={false} width={48} />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  labelStyle={TOOLTIP_LABEL_STYLE}
                  formatter={(v) => [`$${Number(v).toFixed(2)}`, "Balance"]}
                />
                <Area type="monotone" dataKey="balance" stroke="#D6FF38" strokeWidth={2} fill="url(#gLeader)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="border border-[#2A2D3A] bg-[#15171E] rounded-md p-4">
          <div className="text-[10px] mono uppercase tracking-widest text-[#D6FF38]/70 mb-3">Fleet Status</div>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={statusData} dataKey="value" innerRadius={45} outerRadius={75} paddingAngle={2} stroke="#0B0C10">
                  {statusData.map((d) => (
                    <Cell key={d.name} fill={STATUS_COLORS[d.name] || "#94A3B8"} />
                  ))}
                </Pie>
                <Tooltip contentStyle={TOOLTIP_STYLE} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-1.5 mt-2">
            {statusData.map((s) => (
              <div key={s.name} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-sm" style={{ background: STATUS_COLORS[s.name] }} />
                  <span className="text-[#94A3B8] capitalize">{s.name.replace(/_/g, " ")}</span>
                </div>
                <span className="mono text-[#F1F5F9]">{s.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Fleet Heatmap - symbol x tier grid */}
      <FleetHeatmap limit={12} />

      {/* Top 10 + recent AI decisions */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
        <div className="lg:col-span-3 border border-[#2A2D3A] bg-[#15171E] rounded-md p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] mono uppercase tracking-widest text-[#D6FF38]/70">Top 10 Candidates</div>
            <Link to="/leaderboard" className="text-xs mono uppercase tracking-widest text-[#D6FF38] flex items-center gap-1 hover:gap-2 transition-all">
              full leaderboard <ArrowRightIcon size={12} />
            </Link>
          </div>
          <table className="w-full text-sm" data-testid="top10-table">
            <thead>
              <tr className="text-[10px] mono uppercase tracking-widest text-[#64748B] border-b border-[#2A2D3A]">
                <th className="text-left py-2 font-normal">Bot</th>
                <th className="text-left py-2 font-normal">Symbol</th>
                <th className="text-left py-2 font-normal">Status</th>
                <th className="text-right py-2 font-normal">Final Balance</th>
                <th className="text-right py-2 font-normal">Growth %</th>
                <th className="text-right py-2 font-normal">Months</th>
                <th className="text-right py-2 font-normal">Win-Months</th>
              </tr>
            </thead>
            <tbody>
              {top10.map((b, i) => (
                <tr key={b.id} className="border-b border-[#2A2D3A]/40 hover:bg-white/[0.02] transition-colors row-fade-in" style={{ animationDelay: `${i * 30}ms` }}>
                  <td className="py-2.5">
                    <Link to={`/bot/${b.id}`} data-testid={`top10-bot-${i}`} className="mono text-[#F1F5F9] hover:text-[#D6FF38]">{b.name}</Link>
                  </td>
                  <td className="py-2.5 mono text-[#94A3B8]">{b.symbol}</td>
                  <td className="py-2.5"><StatusBadge status={b.status} /></td>
                  <td className="py-2.5 mono text-right">${(b.final_balance || 0).toLocaleString()}</td>
                  <td className="py-2.5 mono text-right" style={{ color: b.growth_pct >= 0 ? "#2ECA7F" : "#FF4D4D" }}>
                    {fmt.signed(b.growth_pct)}%
                  </td>
                  <td className="py-2.5 mono text-right text-[#94A3B8]">{b.months_tested}</td>
                  <td className="py-2.5 mono text-right text-[#94A3B8]">{b.profitable_months}/{b.months_tested}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="border border-[#2A2D3A] bg-[#15171E] rounded-md p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] mono uppercase tracking-widest text-[#D6FF38]/70">AI Decisions</div>
            <Link to="/decisions" className="text-xs mono uppercase tracking-widest text-[#D6FF38]">all</Link>
          </div>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {recent_decisions.map((d) => (
              <div key={d.id} className="border-l-2 border-[#2A2D3A] pl-3 hover:border-[#D6FF38] transition-colors" data-testid={`recent-decision-${d.id}`}>
                <div className="flex items-center gap-2">
                  <BrainIcon size={12} className="text-[#D6FF38]" />
                  <span className="text-[10px] mono uppercase tracking-widest text-[#64748B]">{d.decision_type}</span>
                </div>
                <div className="text-sm mt-0.5 text-[#F1F5F9]">{d.title}</div>
                {d.bot_name && (
                  <div className="text-[11px] mono text-[#94A3B8] mt-0.5">→ {d.bot_name}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mutation targets + symbol heat */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="border border-[#2A2D3A] bg-[#15171E] rounded-md p-4">
          <div className="text-[10px] mono uppercase tracking-widest text-[#64748B] mb-3 flex items-center gap-2">
            <ClockClockwiseIcon size={12} /> Next Mutation Targets
          </div>
          <div className="space-y-2">
            {mutation_targets.map((b, i) => (
              <Link to={`/bot/${b.id}`} key={b.id} data-testid={`mutation-target-${i}`} className="flex items-center justify-between py-2 px-3 rounded border border-transparent hover:border-[#2A2D3A] hover:bg-white/[0.02] transition-all">
                <div className="flex items-center gap-3">
                  <span className="mono text-xs text-[#64748B] w-6">#{i + 1}</span>
                  <div>
                    <div className="mono text-sm">{b.name}</div>
                    <div className="text-[11px] text-[#94A3B8]">{b.symbol} · sharpe {b.sharpe}</div>
                  </div>
                </div>
                <div className="mono text-sm" style={{ color: b.growth_pct >= 0 ? "#2ECA7F" : "#FF4D4D" }}>
                  {fmt.signed(b.growth_pct)}%
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="border border-[#2A2D3A] bg-[#15171E] rounded-md p-4">
          <div className="text-[10px] mono uppercase tracking-widest text-[#64748B] mb-3">Symbol Performance · Avg Growth %</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={symbols} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="2 4" stroke="#2A2D3A" opacity={0.4} vertical={false} />
                <XAxis dataKey="symbol" tickLine={false} axisLine={AXIS_LINE_STROKE} />
                <YAxis tickLine={false} axisLine={false} width={40} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="avg_growth" radius={[3, 3, 0, 0]}>
                  {symbols.map((s) => (
                    <Cell key={s.symbol} fill={s.avg_growth >= 0 ? "#2ECA7F" : "#FF4D4D"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
